import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Star, Sparkles, ShieldCheck, Trash2, Check, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

const TABS = [
    { key: "pending", label: "Pending" },
    { key: "approved", label: "Approved" },
    { key: "rejected", label: "Rejected" },
    { key: "all", label: "All" },
];

const AdminReviews = () => {
    const [tab, setTab] = useState("pending");
    const [reviews, setReviews] = useState([]);
    const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        const params = tab === "all" ? {} : { status: tab };
        const { data } = await api.get("/admin/reviews", { params });
        setReviews(data.reviews || []);
        setCounts(data.counts || counts);
        setLoading(false);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

    const update = async (r, patch) => {
        try {
            const { data } = await api.patch(`/admin/reviews/${r.id}`, patch);
            setReviews((xs) => xs.map((x) => x.id === r.id ? { ...x, ...data } : x));
            // Refresh counts if status changed
            if (patch.status) load();
            else toast.success("Updated");
        } catch { toast.error("Failed"); }
    };

    const del = async (r) => {
        if (!confirm(`Delete review by ${r.name}?`)) return;
        try {
            await api.delete(`/admin/reviews/${r.id}`);
            setReviews((xs) => xs.filter((x) => x.id !== r.id));
            toast.success("Deleted");
        } catch { toast.error("Failed"); }
    };

    return (
        <div className="p-8" data-testid="admin-reviews">
            <div className="mb-6">
                <h1 className="font-display text-3xl font-bold tracking-tight">Reviews</h1>
                <p className="text-sm text-muted-foreground">Moderate customer reviews for the public site.</p>
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-5">
                {TABS.map((t) => {
                    const active = tab === t.key;
                    const count = counts[t.key];
                    return (
                        <button
                            key={t.key}
                            onClick={() => setTab(t.key)}
                            data-testid={`admin-reviews-tab-${t.key}`}
                            className={`px-4 h-10 rounded-full text-sm font-semibold flex items-center gap-2 ${active ? "bg-primary text-primary-foreground" : "bg-card border border-border hover:border-primary/40"}`}
                        >
                            {t.label}
                            {count !== undefined && (
                                <span className={`text-[11px] px-2 py-0.5 rounded-full ${active ? "bg-white/20" : "bg-muted"}`}>{count}</span>
                            )}
                        </button>
                    );
                })}
            </div>

            {loading ? <Loader2 className="w-6 h-6 animate-spin text-primary" /> : reviews.length === 0 ? (
                <div className="rounded-2xl bg-card border p-12 text-center text-sm text-muted-foreground">No {tab} reviews.</div>
            ) : (
                <div className="grid md:grid-cols-2 gap-4">
                    {reviews.map((r) => (
                        <div key={r.id} className="rounded-2xl bg-card border p-5" data-testid={`admin-review-${r.id}`}>
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex gap-0.5">
                                    {[1,2,3,4,5].map((i) => <Star key={i} className={`w-4 h-4 ${i <= r.rating ? "fill-brand-gold text-brand-gold" : "text-muted-foreground/30"}`} />)}
                                </div>
                                <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${
                                    r.status === "approved" ? "bg-emerald-100 text-emerald-700" :
                                    r.status === "rejected" ? "bg-destructive/10 text-destructive" :
                                    "bg-yellow-100 text-yellow-700"
                                }`}>{r.status}</span>
                            </div>
                            {r.title && <h3 className="font-semibold text-base">{r.title}</h3>}
                            <p className="text-sm text-foreground/80 mt-1 leading-relaxed">"{r.body}"</p>

                            <div className="mt-3 text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
                                <span className="font-semibold text-foreground">{r.name}</span>
                                {r.city && <span>· {r.city}</span>}
                                {r.email && <span>· {r.email}</span>}
                                {r.product_name && <span>· {r.product_name}</span>}
                                {r.verified_purchase && <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full"><ShieldCheck className="w-3 h-3" /> Verified</span>}
                                {r.is_featured && <span className="inline-flex items-center gap-1 bg-brand-gold text-primary-foreground px-2 py-0.5 rounded-full"><Sparkles className="w-3 h-3" /> Featured</span>}
                            </div>

                            <div className="mt-4 flex flex-wrap gap-2">
                                {r.status !== "approved" && (
                                    <Button size="sm" onClick={() => update(r, { status: "approved" })} data-testid={`approve-${r.id}`} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white h-8"><Check className="w-3.5 h-3.5" /> Approve</Button>
                                )}
                                {r.status !== "rejected" && (
                                    <Button size="sm" variant="outline" onClick={() => update(r, { status: "rejected" })} className="rounded-full h-8"><X className="w-3.5 h-3.5" /> Reject</Button>
                                )}
                                <Button size="sm" variant="outline" onClick={() => update(r, { is_featured: !r.is_featured })} className="rounded-full h-8"><Sparkles className="w-3.5 h-3.5" /> {r.is_featured ? "Unfeature" : "Feature"}</Button>
                                <Button size="sm" variant="outline" onClick={() => update(r, { verified_purchase: !r.verified_purchase })} className="rounded-full h-8"><ShieldCheck className="w-3.5 h-3.5" /> {r.verified_purchase ? "Un-verify" : "Verify"}</Button>
                                <button onClick={() => del(r)} className="ml-auto text-destructive text-xs hover:underline flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default AdminReviews;
